import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { Plus } from 'lucide-react';

const inr = v => `₹${Number(v||0).toLocaleString('en-IN', {minimumFractionDigits:2})}`;

const SLAB_TYPES = [
  { val:'REGULAR',   label:'Regular',   color:'var(--green)',  bg:'#EAF3DE' },
  { val:'IMPERFECT', label:'Imperfect', color:'var(--amber)',  bg:'#FAEEDA' },
  { val:'OLD_STOCK', label:'Old Stock', color:'var(--brand)',  bg:'var(--brand-dim)' },
];

const FINISHES   = ['POLISHED','ROUGH','FLAMED','SANDBLASTED'];
const GRADES     = ['A','B','C'];
const STATUSES   = ['IN_STOCK','PASSED'];

const EMPTY_SLAB = {
  slab_code:'', length:'', width:'', thickness:'',
  quality_grade:'A', finish:'POLISHED', price_per_sqft:'',
  slab_type:'REGULAR', discount_percent:'0',
  color:'', quantity:'1', condition_notes:'', source_reference:'',
  notes:'', status:'PASSED',
};

export default function StockPage() {
  const [stock, setStock]       = useState([]);
  const [slabs, setSlabs]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]         = useState(EMPTY_SLAB);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');
  const [error, setError]       = useState('');
  const [filterType, setFilterType] = useState('');

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const load = useCallback(() => {
    setLoading(true);
    let url = `/production/slabs/?status=PASSED&page_size=200`;
    if(filterType) url += `&slab_type=${filterType}`;
    Promise.all([
      api.get('/production/stock/'),
      api.get(url),
    ]).then(([s,sl]) => {
      setStock(s.data.results||[]);
      setSlabs(sl.data.results||[]);
    }).finally(() => setLoading(false));
  }, [filterType]);

  useEffect(() => { load(); }, [load]);

  // Auto-generate slab code
  const generateCode = async () => {
    const prefix = form.slab_type==='OLD_STOCK'?'OLD':form.slab_type==='IMPERFECT'?'IMP':'SLB';
    try {
      const res = await api.get(`/production/slabs/?page_size=1&ordering=-slab_code`);
      const existing = (res.data.results||[]).filter(s=>s.slab_code?.startsWith(prefix));
      const lastNum = existing.length > 0 
        ? Math.max(...existing.map(s=>parseInt(s.slab_code?.split('/')?.[2]||0))) 
        : 0;
      const nextNum = String(lastNum + 1).padStart(3, '0');
      set('slab_code', `${prefix}/2026/${nextNum}`);
    } catch {
      set('slab_code', `${prefix}/2026/001`);
    }
  };

  const handleSave = async e => {
    e.preventDefault();
    if(!form.slab_code)  { setError('Slab code is required.'); return; }
    if(!form.length)     { setError('Length is required.'); return; }
    if(!form.width)      { setError('Width is required.'); return; }
    setSaving(true); setError('');
    try {
      await api.post('/production/slabs/', {
        ...form,
        status: 'PASSED',
        manually_added: true,
      });
      setMsg('Slab added to stock!');
      setShowModal(false);
      setForm(EMPTY_SLAB);
      load();
      setTimeout(()=>setMsg(''), 3000);
    } catch(err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to save.');
    } finally { setSaving(false); }
  };

  // Calculate area
  const area = form.length && form.width
    ? ((parseFloat(form.length) * parseFloat(form.width)) / 929.03).toFixed(2)
    : null;

  const totalSlabs     = slabs.length;
  const regularSlabs   = slabs.filter(s=>s.slab_type==='REGULAR'||!s.slab_type).length;
  const imperfectSlabs = slabs.filter(s=>s.slab_type==='IMPERFECT').length;
  const oldSlabs       = slabs.filter(s=>s.slab_type==='OLD_STOCK').length;

  return (
    <Layout title="Stock" subtitle="Available slabs inventory"
      actions={
        <button className="btn btn-primary" onClick={()=>{setForm(EMPTY_SLAB);setError('');setShowModal(true);}}>
          <Plus size={14}/>Add Stock Manually
        </button>
      }
    >
      {msg   && <div className="success-box">{msg}</div>}
      {error && <div className="error-box">{error}</div>}

      {/* KPIs */}
      <div className="kpi-grid" style={{gridTemplateColumns:'repeat(4,1fr)', marginBottom:16}}>
        <div className="kpi-card brand"><div className="kpi-value">{totalSlabs}</div><div className="kpi-label">Total in Stock</div></div>
        <div className="kpi-card green"><div className="kpi-value">{regularSlabs}</div><div className="kpi-label">Regular Slabs</div></div>
        <div className="kpi-card amber"><div className="kpi-value">{imperfectSlabs}</div><div className="kpi-label">Imperfect Slabs</div></div>
        <div className="kpi-card blue"><div className="kpi-value">{oldSlabs}</div><div className="kpi-label">Old Stock Slabs</div></div>
      </div>

      {/* Stock Summary Cards */}
      {stock.length > 0 && (
        <div className="kpi-grid" style={{marginBottom:16}}>
          {stock.map((s,i) => (
            <div key={i} className="kpi-card blue">
              <div className="kpi-value">{s.available_slabs}</div>
              <div className="kpi-label">{s.color} — Grade {s.quality_grade||'N/A'}</div>
              <div className="kpi-sub">{s.sold_slabs} sold · {s.total_slabs} total</div>
            </div>
          ))}
        </div>
      )}

      {/* Slab List */}
      <div className="card">
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:'1.2px',textTransform:'uppercase',color:'var(--brand)'}}>
            Available Slabs <span className="badge badge-green" style={{marginLeft:8}}>{slabs.length} in stock</span>
          </div>
          <div style={{display:'flex',gap:8}}>
            <select className="form-input" style={{width:'auto',minWidth:150}} value={filterType} onChange={e=>setFilterType(e.target.value)}>
              <option value="">All Types</option>
              {SLAB_TYPES.map(t=><option key={t.val} value={t.val}>{t.label}</option>)}
            </select>
          </div>
        </div>

        {loading ? <div className="loading"><div className="spinner"/>Loading...</div> :
          slabs.length===0 ? <div className="empty-state"><p>No slabs in stock.</p></div> :
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Slab Code</th>
                  <th>Type</th>
                  <th>Size (cm)</th>
                  <th>Thickness</th>
                  <th>Area (sqft)</th>
                  <th>Grade</th>
                  <th>Finish</th>
                  <th>Price/sqft</th>
                  <th>Discount</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {slabs.map(s => {
                  const slabType = SLAB_TYPES.find(t=>t.val===s.slab_type) || SLAB_TYPES[0];
                  return (
                    <tr key={s.id}>
                      <td style={{color:'var(--brand)',fontWeight:600,fontFamily:'DM Mono,monospace',fontSize:12}}>{s.slab_code}</td>
                      <td>
                        <span style={{fontSize:11,padding:'2px 8px',borderRadius:20,fontWeight:600,
                          background:slabType.bg,color:slabType.color,border:`1px solid ${slabType.color}40`}}>
                          {slabType.label}
                        </span>
                      </td>
                      <td style={{fontFamily:'DM Mono,monospace',fontSize:12}}>{s.length}×{s.width}</td>
                      <td style={{fontSize:12}}>{s.thickness?`${s.thickness}mm`:'—'}</td>
                      <td style={{fontFamily:'DM Mono,monospace',fontSize:12}}>{s.area_sqft||'—'}</td>
                      <td><span className="badge badge-gold">{s.quality_grade||'—'}</span></td>
                      <td><span className="badge badge-blue">{s.finish}</span></td>
                      <td style={{fontFamily:'DM Mono,monospace',fontSize:12}}>{s.price_per_sqft?inr(s.price_per_sqft):'—'}</td>
                      <td style={{fontSize:12,color:'var(--red)'}}>{s.discount_percent&&parseFloat(s.discount_percent)>0?`${s.discount_percent}%`:'—'}</td>
                      <td style={{fontSize:12,color:'var(--text-muted)',maxWidth:150,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.notes||'—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        }
      </div>

      {/* Add Stock Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal" style={{maxWidth:580,maxHeight:'90vh',overflowY:'auto'}}>
            <div className="modal-header">
              <h3>Add Stock Manually</h3>
              <button onClick={()=>setShowModal(false)} style={{background:'none',border:'none',cursor:'pointer',fontSize:18}}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                {error && <div className="error-box">{error}</div>}

                <div style={{fontSize:11,fontWeight:700,letterSpacing:'1.2px',textTransform:'uppercase',color:'var(--brand)',borderBottom:'2px solid var(--border)',paddingBottom:6,marginBottom:14}}>
                  Slab Details
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Slab Type *</label>
                    <select className="form-input" value={form.slab_type} onChange={e=>{set('slab_type',e.target.value); setTimeout(generateCode,100);}}>
                      {SLAB_TYPES.map(t=><option key={t.val} value={t.val}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Slab Code *</label>
                    <div style={{display:'flex',gap:6}}>
                      <input className="form-input" value={form.slab_code} onChange={e=>set('slab_code',e.target.value)} placeholder="e.g. OLD/2026/001"/>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={generateCode} style={{whiteSpace:'nowrap'}}>Auto</button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Length (cm) *</label>
                    <input className="form-input" type="number" step="0.01" value={form.length} onChange={e=>set('length',e.target.value)} placeholder="e.g. 250"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Width (cm) *</label>
                    <input className="form-input" type="number" step="0.01" value={form.width} onChange={e=>set('width',e.target.value)} placeholder="e.g. 120"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Thickness (mm)</label>
                    <input className="form-input" type="number" step="0.1" value={form.thickness} onChange={e=>set('thickness',e.target.value)} placeholder="e.g. 20"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Quality Grade</label>
                    <select className="form-input" value={form.quality_grade} onChange={e=>set('quality_grade',e.target.value)}>
                      {GRADES.map(g=><option key={g} value={g}>Grade {g}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Finish</label>
                    <select className="form-input" value={form.finish} onChange={e=>set('finish',e.target.value)}>
                      {FINISHES.map(f=><option key={f}>{f}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price per Sqft (₹)</label>
                    <input className="form-input" type="number" step="0.01" value={form.price_per_sqft} onChange={e=>set('price_per_sqft',e.target.value)} placeholder="0.00"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Color</label>
                    <input className="form-input" value={form.color||''} onChange={e=>set('color',e.target.value)} placeholder="e.g. Black, Grey, White"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Quantity (NOS)</label>
                    <input className="form-input" type="number" min="1" value={form.quantity||1} onChange={e=>set('quantity',e.target.value)} placeholder="1"/>
                  </div>
                  {form.slab_type!=='REGULAR' && (
                    <div className="form-group">
                      <label className="form-label">Discount %</label>
                      <input className="form-input" type="number" step="0.01" min="0" max="100" value={form.discount_percent} onChange={e=>set('discount_percent',e.target.value)} placeholder="0"/>
                    </div>
                  )}
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label className="form-label">Source / Origin</label>
                    <input className="form-input" value={form.source_reference||''} onChange={e=>set('source_reference',e.target.value)} placeholder="e.g. Old quarry stock, Block N-BOX/0001"/>
                  </div>
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label className="form-label">Condition Notes</label>
                    <textarea className="form-input" rows={2} value={form.condition_notes||''} onChange={e=>set('condition_notes',e.target.value)} placeholder="e.g. Minor crack on edge, slightly uneven surface"/>
                  </div>
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label className="form-label">Notes</label>
                    <textarea className="form-input" rows={2} value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Reason for manual entry, additional details"/>
                  </div>
                </div>

                {/* Auto calculated area */}
                {area && (
                  <div style={{padding:'10px 14px',background:'var(--brand-dim)',borderRadius:'var(--radius-sm)',border:'1px solid var(--border)',marginTop:8}}>
                    <span style={{fontSize:12,color:'var(--text-muted)'}}>Calculated Area: </span>
                    <span style={{fontSize:14,fontWeight:700,color:'var(--brand)',fontFamily:'DM Mono,monospace'}}>{area} sqft</span>
                    {form.price_per_sqft && (
                      <>
                        <span style={{fontSize:12,color:'var(--text-muted)',marginLeft:16}}>Estimated Value: </span>
                        <span style={{fontSize:14,fontWeight:700,color:'var(--green)',fontFamily:'DM Mono,monospace'}}>
                          {inr(parseFloat(area) * parseFloat(form.price_per_sqft))}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Saving...':'Add to Stock'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}